import type { DisplayPort } from "utils/environment/networkAccessFallback";

export type SecureLinkAccessLevel = "public" | "authenticated" | "inaccessible";
export type SecureLinkAccessAudience =
  | "public"
  | "any-authenticated"
  | "specified-users";

type SecureLinkAccessPort = Pick<
  DisplayPort,
  "allowPublicUnauthenticated" | "authorizedEmails"
>;

type SecureLinkAccessUser = {
  email?: unknown;
  externalAuthId?: unknown;
  externalIdentities?: unknown;
} | null;

const getIdentityValueFromRecord = (identity: Record<string, unknown>) =>
  identity.externalId ??
  identity.external_id ??
  identity.externalAuthId ??
  identity.identityId ??
  identity.identity_id;

const compactIdentity = (identity: unknown) => {
  if (typeof identity === "string") {
    const trimmed = identity.trim();
    return trimmed || undefined;
  }

  if (typeof identity === "object" && identity !== null) {
    return compactIdentity(
      getIdentityValueFromRecord(identity as Record<string, unknown>),
    );
  }

  return undefined;
};

const compactIdentities = (identities: readonly unknown[]) =>
  identities
    .map((identity) => compactIdentity(identity))
    .filter((identity): identity is string => Boolean(identity));

const getAuthorizedAccessIdentities = (port: SecureLinkAccessPort) =>
  compactIdentities(port.authorizedEmails ?? []);

export const getSecureLinkAccessAudience = (
  port: SecureLinkAccessPort,
): SecureLinkAccessAudience => {
  if (port.allowPublicUnauthenticated) return "public";
  return getAuthorizedAccessIdentities(port).length > 0
    ? "specified-users"
    : "any-authenticated";
};

export const getCurrentUserAccessIdentities = (
  user: SecureLinkAccessUser | undefined,
): string[] => {
  const runtimeExternalIdentities = user?.externalIdentities;
  const externalIdentities = Array.isArray(runtimeExternalIdentities)
    ? runtimeExternalIdentities
    : [];
  const identities = compactIdentities([
    user?.externalAuthId,
    ...externalIdentities,
    user?.email,
  ]);

  return Array.from(new Set(identities));
};

export const userCanAccessSecureLink = (
  port: SecureLinkAccessPort,
  userIdentities: readonly string[],
): boolean => {
  if (port.allowPublicUnauthenticated) return true;

  const identities = new Set(compactIdentities(userIdentities));
  if (identities.size === 0) return false;

  const authorizedIdentities = getAuthorizedAccessIdentities(port);
  if (authorizedIdentities.length === 0) return true;

  return authorizedIdentities.some((identity) => identities.has(identity));
};

export const getSecureLinkAccessLevel = (
  port: SecureLinkAccessPort,
  userIdentities: readonly string[],
): SecureLinkAccessLevel => {
  if (port.allowPublicUnauthenticated) return "public";
  return userCanAccessSecureLink(port, userIdentities)
    ? "authenticated"
    : "inaccessible";
};
